<template>
  <div>
    <span v-if="mini">
      <v-avatar size="40">
        <img
          :src="user.organisation_settings.logo_url"
        >
      </v-avatar>
    </span>
    <v-select
      v-else
      v-model="selectedTeam"
      :items="selectTeamItems"
      :loading="loading"
      return-object
      item-value="id"
      item-text="name"
      :label="$t('table_headers.admin_staff.teams')"
      background-color="white"
      solo
      dense
      outlined
      hide-details
      style="width: 300px"
    >
      <template v-slot:selection="{item}">
        <v-avatar size="30">
          <img
            :src="user.organisation_settings.logo_url"
          >
        </v-avatar>
        <span
          class="ml-2 font-weight-bold"
          style="max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
        >{{ item.short_name }}</span>
      </template>
      <template v-slot:item="{item}">
        <v-avatar size="30">
          <img
            :src="user.organisation_settings.logo_url"
          >
        </v-avatar>
        <span
          class="ml-2"
          style="max-width: 160px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis"
        >{{ item.short_name }}</span>
      </template>
    </v-select>
  </div>
</template>

<script>

import { QuestionnaireApiService } from '@/data/services'
import { QuestionnaireType } from '@/data/models/Questionnaire'

export default {
  name: 'TeamSelectMenu',
  props: {
    allTeams: {
      type: Boolean,
      default: false
    },
    mini: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      wellnessAccess: {
        loading: false,
        canView: null
      }
    }
  },
  computed: {
    selectedTeam: {
      get: function() {
        return this.$store.getters[this.$storeGetters.APP_CURRENT_TEAM]
      },
      set: async function(val) {
        const wasAdminRoute = typeof this.$route.name === 'string' && this.$route.name.startsWith('admin')

        if (this.$route.name === this.$routeNames.ADMIN_PLAYER_EDIT) {
          this.$router.push({ name: this.$routeNames.ADMIN_PLAYERS })
        } else if (this.$route.name === this.$routeNames.PLAYER_DETAILS) {
          this.$router.go(-1)
        }
        this.$store.dispatch(this.$storeActions.APP_SET_CURRENT_TEAM, val)

        if (!val) return

        await this.refreshWellnessAccess()

        const teamId = val.id !== 'all' ? val.id : null
        const routeName = this.$route.name

        const canAccessRouteForTeam = () => {
          if (wasAdminRoute) {
            if (val.id === 'all') return true
            return this.$can('manage-team-players', teamId)
          }

          if (routeName === this.$routeNames.LOGIN || routeName === this.$routeNames.LOGOUT) return true

          if (routeName === this.$routeNames.EVENTS) return true

          if (routeName === this.$routeNames.DASHBOARD_PLAYERS) return true

          const performanceRoutes = [
            this.$routeNames.OVERVIEW,
            this.$routeNames.PERFORMANCE_EVENT,
            this.$routeNames.PERFORMANCE_EVENT_PLAYER,
            this.$routeNames.PERFORMANCE_ADVANCED,
            this.$routeNames.PERFORMANCE_WEEKLY_LOAD,
            this.$routeNames.PERFORMANCE_PERIOD_SUMMARY,
            this.$routeNames.DAILY_ACTIVITY,
            this.$routeNames.PLAYER_OVERVIEW,
            this.$routeNames.PLAYER_LOAD,
            this.$routeNames.PLAYER_DAILY_ACTIVITY,
            this.$routeNames.PLAYER_EVENTS
          ]
          if (performanceRoutes.includes(routeName)) {
            return this.$can('view-performance', teamId)
          }

          const diagnosticsRoutes = [
            this.$routeNames.DIAGNOSTICS,
            this.$routeNames.DIAGNOSTIC_PROTOCOLS,
            this.$routeNames.DIAGNOSTIC_COMPARISON,
            this.$routeNames.PLAYER_DIAGNOSTICS
          ]
          if (diagnosticsRoutes.includes(routeName)) {
            return this.$can('view-diagnostics', teamId)
          }

          const medicalRoutes = [
            this.$routeNames.MEDICAL,
            this.$routeNames.PLAYER_MEDICAL,
            this.$routeNames.PLAYER_WELLNESS
          ]
          if (medicalRoutes.includes(routeName)) {
            return (this.$can('view-medical', teamId) || this.$can('view-basic-medical', teamId)) && this.hasMedicalModule
          }

          if (routeName === this.$routeNames.WELLNESS) {
            return this.hasItlModule && this.canViewWellnessForTeam
          }

          if (routeName === this.$routeNames.LOAD) {
            return (this.hasItlModule || this.hasGpsModule || this.hasHrModule) && this.$can('view-performance', teamId)
          }

          if (routeName === this.$routeNames.EVENT_STATS) {
            return this.hasEventStats && this.$can('view-medical', teamId)
          }

          const trainingRoutes = [
            this.$routeNames.TRAINING_PROTOCOLS,
            this.$routeNames.TRAINING_EXERCISES,
            this.$routeNames.SESSIONS_OVERVIEW,
            this.$routeNames.PLAYER_TRAINING_EXERCISES,
            this.$routeNames.PLAYER_SESSIONS,
            this.$routeNames.COACH_INSIGHTS_INDIVIDUAL,
            this.$routeNames.PLAYER_INSIGHTS_INDIVIDUAL
          ]
          if (trainingRoutes.includes(routeName)) {
            return this.$can('view-training-protocols', teamId) && this.hasTrainingBuilderModule
          }

          const eventRoutes = [
            this.$routeNames.PLAYER_ATTENDANCE
          ]
          if (eventRoutes.includes(routeName)) {
            return this.$can('view-event-attendance', teamId)
          }

          const mediaLibraryRoutes = [
            this.$routeNames.MEDIA_LIBRARY
          ]
          if (mediaLibraryRoutes.includes(routeName)) {
            return this.$can('view-player-media', teamId)
          }

          const coachInsightsRoutes = [
            this.$routeNames.COACH_INSIGHTS,
            this.$routeNames.COACH_INSIGHTS_INDIVIDUAL
          ]
          if (coachInsightsRoutes.includes(routeName)) {
            return this.$can('preview-coach-exercise-insights', teamId) && this.hasTrainingBuilderModule
          }

          const playerInsightsRoutes = [
            this.$routeNames.PLAYER_INSIGHTS,
            this.$routeNames.PLAYER_INSIGHTS_INDIVIDUAL
          ]
          if (playerInsightsRoutes.includes(routeName)) {
            return this.$can('preview-player-exercise-insights', teamId) && this.hasTrainingBuilderModule
          }

          const drillRoutes = [
            this.$routeNames.DRILL_BUILDER
          ]
          if (drillRoutes.includes(routeName)) {
            return this.hasDrillModule && this.teamWithDrill && this.$can('view-training-protocols', teamId)
          }

          const trainingProgramsRoutes = [
            this.$routeNames.TRAINING_PROGRAMS,
            this.$routeNames.TRAINING_PROGRAM_INSTANCES
          ]
          if (trainingProgramsRoutes.includes(routeName)) {
            return this.hasTrainingProgramsModule && !this.hasNoPlayers && this.$can('create-event', teamId)
          }
          const scoutingRoutes = [
            this.$routeNames.SCOUTING
          ]
          if (scoutingRoutes.includes(routeName)) {
            return this.user?.can_view_scouting_questionnaire ?? false
          }

          return true
        }

        if (!canAccessRouteForTeam()) {
          if (wasAdminRoute) {
            this.$router.push({ name: this.$routeNames.OVERVIEW })
          } else {
            this.$router.push({ name: this.$routeNames.EVENTS })
          }
        }
      }
    },
    user() {
      return this.$store.getters[this.$storeGetters.AUTH_USER]
    },
    canViewWellnessForTeam() {
      if (this.wellnessAccess.canView !== null) return this.wellnessAccess.canView
      return this.user?.can_view_wellness_questionnaire ?? false
    },
    hasTrainingProgramsModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_training_programs_module
    },
    hasTrainingBuilderModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_training_builder_module
    },
    hasDrillModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_drill_module
    },
    hasItlModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_itl_module
    },
    hasGpsModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_gps_module
    },
    hasHrModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_heart_rate_module
    },
    hasMedicalModule() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_medical_module
    },
    hasEventStats() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_event_stats
    },
    hasNoPlayers() {
      return this.user && this.user.organisation_settings && this.user.organisation_settings.has_no_players
    },
    loading() {
      return this.$store.getters[this.$storeGetters.APP_LOADING]
    },
    teams() {
      return this.$store.getters[this.$storeGetters.APP_TEAMS]
    },
    hasAllTeamsOption() {
      return this.allTeams
    },
    selectTeamItems() {
      if (!this.teams) return
      const selectTeamItems = []

      if (this.hasAllTeamsOption) {
        selectTeamItems.push({ id: 'all', name: this.$t('dropdown.team_selection.all_teams') })
        selectTeamItems.push({ divider: true })
      }

      selectTeamItems.push(...this.teams)

      return selectTeamItems
    },
    teamWithDrill() {
      return ['Football', 'Futsal', 'Handball', 'Basketball', 'Hockey', 'US Football', 'Water Polo', 'Volleyball', 'Badminton', 'Baseball', 'Lacrosse', 'Pickleball', 'Tennis', 'Field Hockey'].includes(this.selectedTeam?.sport?.name) || false
    }
  },
  watch: {
    selectedTeam: {
      handler() {
        this.refreshWellnessAccess()
      },
      immediate: true
    }
  },
  created() {
    // this.selectedTeam = this.selectItems[0]
  },
  methods: {
    async refreshWellnessAccess() {
      const teamId = this.selectedTeam?.id
      const coachId = this.user?.coach?.id

      if (!teamId || !coachId) {
        this.wellnessAccess.canView = null
        return false
      }

      this.wellnessAccess.loading = true

      try {
        const response = await QuestionnaireApiService.find(QuestionnaireType.WELLNESS)
        const permission = response.data?.coach_permissions?.find(cp => cp.team_id === teamId && cp.coach_id === coachId)
        this.wellnessAccess.canView = permission ? permission.can_view : true
        return this.wellnessAccess.canView
      } catch (e) {
        this.wellnessAccess.canView = null
        return false
      } finally {
        this.wellnessAccess.loading = false
      }
    }
  }
}
</script>
